// An operation's status on screen and Job Complete: the confirm step, submitOpen (Ready to Bill,
// shrinkwrap follow-on, canvas and pesticide prompts), changeStatus / updateOperationTableUI (the
// rows' colours and pills), and the phone-width Labor/Parts tabs.
// Moved out of app.js unchanged (docs/split-plan.md, session 7c).
import { offerCanvasMove } from './canvasOpOffer';
import { getJobsByWorkOrder, updateJobStatus } from './jobWrites';
import { laborSummaryFromDOM } from './laborParts';
import { checkForPesticideUsage } from './pesticideLog';
import { getDB, isShrinkwrapInstall, toastMessage } from './utilities';
import { syncOpRowBillButton, woStatusToneSlug } from './woTable';

let shell = null;
export function setOpStatusDeps(deps) { shell = deps; }

// Job complete confirm — a summary step in front of submitOpen() so techs
// aren't marking a job complete blind (hours/parts totals + date range worked,
// computed from what's already rendered in the Labor/Parts cards).
export function openJobCompleteConfirm() {
  const { totalHours } = laborSummaryFromDOM();
  const partsCount = document.querySelectorAll('#parts-table-body-current .part-entry:not(.header)').length;

  const dates = Array.from(document.querySelectorAll('#labor-table-body-current .labor-entry:not(.header)'))
    .map(row => row.dataset.timeSubmitted)
    .filter(Boolean)
    .sort();

  let dateRangeText = '—';
  if (dates.length) {
    const fmt = (d) => {
      const dt = new Date(d);
      return isNaN(dt) ? '' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };
    const first = fmt(dates[0]);
    const last = fmt(dates[dates.length - 1]);
    dateRangeText = (first && last) ? (first === last ? first : `${first}–${last.split(' ').pop()}`) : (first || last || '—');
  }

  document.getElementById('jobCompleteHours').textContent = totalHours.toFixed(1);
  document.getElementById('jobCompleteParts').textContent = partsCount;
  document.getElementById('jobCompleteDates').textContent = dateRangeText;

  document.getElementById('job-complete-confirm').style.display = 'flex';
}

document.getElementById('job-complete-confirm-cancel').addEventListener('click', () => {
  document.getElementById('job-complete-confirm').style.display = 'none';
});

document.getElementById('job-complete-confirm-yes').addEventListener('click', () => {
  document.getElementById('job-complete-confirm').style.display = 'none';
  submitOpen();
});

// Phone width — tabs replace the side-by-side Labor/Parts cards; the sticky
// bottom bar's buttons just proxy the real desktop buttons' clicks so there's
// only one set of handlers to keep straight.
function setOpDetailTab(tab) {
  const isParts = tab === 'parts';
  document.getElementById('op-detail-tab-labor').classList.toggle('active', !isParts);
  document.getElementById('op-detail-tab-parts').classList.toggle('active', isParts);
  document.querySelector('.op-card-labor').classList.toggle('op-tab-inactive', isParts);
  document.querySelector('.op-card-parts').classList.toggle('op-tab-inactive', !isParts);
  document.getElementById('op-detail-mobile-bar-labor').style.display = isParts ? 'none' : '';
  document.getElementById('op-detail-mobile-bar-parts').style.display = isParts ? '' : 'none';
}

document.getElementById('op-detail-tab-labor').addEventListener('click', () => setOpDetailTab('labor'));
document.getElementById('op-detail-tab-parts').addEventListener('click', () => setOpDetailTab('parts'));

document.getElementById('open-add-labor-mobile').addEventListener('click', () => document.getElementById('open-add-labor').click());
document.getElementById('job-complete-mobile').addEventListener('click', () => document.querySelector('.job-complete-button').click());
document.getElementById('open-part-kit-mobile').addEventListener('click', () => document.getElementById('open-part-kit').click());
document.getElementById('open-part-order-mobile').addEventListener('click', () => document.getElementById('open-part-order').click());
document.getElementById('open-add-parts-mobile').addEventListener('click', () => document.getElementById('open-add-parts').click());

export async function submitOpen(outsideId, outsideWorkOrder, { fromListener = false } = {}) {
  try {
    const id = outsideId ? outsideId : document.getElementById('currentJobId').value;

    toastMessage('Marking Job Complete', 'blue');

    const data = {
      Status: 'Ready to Bill',
      Work_Order: outsideWorkOrder ? outsideWorkOrder : document.getElementById('updateWorkOrder').value,
    };

    await changeStatus(id, 'Ready to Bill');
    await updateJobStatus(id, 'Ready to Bill');
    await checkForShrinkwrap(id);
    await updateOperationTableUI(id, data);

    const db = await getDB();
    const tx = db.transaction("jobData", "readwrite");
    const tableStore = tx.objectStore("jobData");

    const record = await new Promise((resolve, reject) => {
      const req = tableStore.get(id);
      req.onerror = (event) => {
        console.error("Error fetching record:", event.target.errorCode);
        reject(event.target.errorCode);
      };
      req.onsuccess = () => resolve(req.result || null);
    });

    if (record) {
      record.Status = "Ready to Bill";
      record.Operation_Dependency = 0;

      await new Promise((resolve, reject) => {
        const req = tableStore.put(record);
        req.onerror = (event) => {
          console.error("Error updating record:", event.target.errorCode);
          reject(event.target.errorCode);
        };
        req.onsuccess = () => resolve();
      });
    }

    toastMessage("Job Marked Complete", 'green');
    // A realtime change (often from another device) must not close whatever operation this
    // person has open — only the panel showing the op that was just completed.
    if (!fromListener || document.getElementById('currentJobId')?.value === id) {
      document.getElementById('statusTable').style.display = 'none';
      document.getElementById('Update').classList.remove('active');
      const completeBtn = document.querySelector('.job-complete-button');
      if (completeBtn) completeBtn.style.display = "none";
    }

    const totalStatus = await shell.checkOverallStatus();

    // A canvas op (remove / send out / reinstall): offer the same move for the boat's pieces.
    if (!fromListener && record) await offerCanvasMove(record, { toast: msg => toastMessage(msg, 'green') });

    await checkForPesticideUsage(id, record);
  } catch (error) {
    console.error('Error submitting work order:', error);
  }
}

async function checkForShrinkwrap(id) {
  return new Promise(async (resolve, reject) => {
    try {
      const db = await getDB();
      const transaction = db.transaction("jobData", "readonly");
      const jobDataStore = transaction.objectStore("jobData");

      const job = await new Promise((res, rej) => {
        const req = jobDataStore.get(id);
        req.onerror = () => {
          console.error("Failed to fetch the job with the provided ID.");
          rej();
        };
        req.onsuccess = () => res(req.result || null);
      });

      if (!job) {
        console.error("Job not found.");
        resolve(); // don't hang caller
        return;
      }

      const workOrder = job.Work_Order;

      const allJobs = await new Promise((res, rej) => {
        const req = jobDataStore.getAll();
        req.onerror = () => {
          console.error("Failed to fetch jobs for the Work_Order.");
          rej();
        };
        req.onsuccess = () => res(req.result || []);
      });

      const matchingJobs = allJobs.filter(j => j.Work_Order === workOrder);

      const nonShrinkwrapJobs = matchingJobs.filter(j => !isShrinkwrapInstall(j));

      const allComplete = nonShrinkwrapJobs.every(
        j =>
          j.Status === "Ready to Bill" ||
          j.Status === "Complete" ||
          j.Status === "Billed" ||
          j.Status === "Closed"
      );

      let didUpdate = false;

      matchingJobs.forEach(matchingJob => {
        if (isShrinkwrapInstall(matchingJob)) {
          if (matchingJob.Status === "On Work Schedule" && allComplete) {
            changeStatus(matchingJob.id, "Ready to Frame");
            updateJobStatus(matchingJob.id, "Ready to Frame");
            didUpdate = true;
          }
        }
      });

      resolve();
    } catch (error) {
      console.error("Failed to open IndexedDB.", error);
      reject(error);
    }
  });
}


export async function changeStatus(id, status) {

  new Promise((resolve) => {
  const jobList = document.querySelectorAll('.job-item[data-id="' + id + '"]');

  if(status === 'Ready to Bill' || status === 'Complete'){
    jobList.forEach(job => {

      if(job.querySelector('.status-info-p')){
        job.querySelector('.status-info-p').innerHTML = 'Ready to Bill';
        job.querySelector('.status-info-p').className = 'status-info-p wo-tone-' + woStatusToneSlug('Ready to Bill');
      }

      if(job.querySelector('.status-info')){
        job.querySelector('.status-info').style.backgroundColor = 'red';
        /* job.style.outline = '2px solid red';  */
      }

      job.classList.add('complete-job');
      job.style.display='none';
      const currentData = JSON.parse(job.getAttribute('data-jobinfo'));
      currentData.Status = 'Ready to Bill';
      currentData.Operation_Dependency = 0;
      job.setAttribute('data-jobinfo', JSON.stringify(currentData));
    });
    resolve();
  }

  if(status === 'In Progress'){
    jobList.forEach(job => {

      if(job.querySelector('.status-info-p')){
        job.querySelector('.status-info-p').innerHTML = 'In Progress';
        job.querySelector('.status-info-p').className = 'status-info-p wo-tone-' + woStatusToneSlug('In Progress');
      }

      if(job.querySelector('.status-info')){
        job.querySelector('.status-info').style.backgroundColor = 'yellow';
        /* job.style.outline = '2px solid yellow'; */
      }

      const currentData = JSON.parse(job.getAttribute('data-jobinfo'));
      currentData.Status = 'In Progress';
      currentData.Operation_Dependency = 0;
      job.setAttribute('data-jobinfo', JSON.stringify(currentData));
    });
    resolve();
  }

  if(status === 'On Work Schedule'){
    jobList.forEach(job => {
      if(job.querySelector('.status-info-p')){
        job.querySelector('.status-info-p').innerHTML = 'Needs Scheduling';
        job.querySelector('.status-info-p').className = 'status-info-p wo-tone-' + woStatusToneSlug('On Work Schedule');
      }
      if(job.querySelector('.status-info')){
        job.querySelector('.status-info').style.backgroundColor = 'green';
        /* job.style.outline = '2px solid green'; */
      }
      const currentData = JSON.parse(job.getAttribute('data-jobinfo'));
      currentData.Status = 'On Work Schedule';
      job.setAttribute('data-jobinfo', JSON.stringify(currentData));
    });
    resolve();
  }

  if(status === 'Hold'){
    jobList.forEach(job => {
      if(job.querySelector('.status-info-p')){
        job.querySelector('.status-info-p').innerHTML = 'Hold';
        job.querySelector('.status-info-p').className = 'status-info-p wo-tone-' + woStatusToneSlug('Hold');
      }
      if(job.querySelector('.status-info')){
        job.querySelector('.status-info').style.backgroundColor = 'lightgrey';
        /* job.style.outline = '2px solid lightgrey'; */
      }
      const currentData = JSON.parse(job.getAttribute('data-jobinfo'));
      currentData.Status = 'Hold';
      job.setAttribute('data-jobinfo', JSON.stringify(currentData));
    });
    resolve();
  }

  if(status === 'Closed'){
    jobList.forEach(job => {
      if(job.querySelector('.status-info-p')){
        job.querySelector('.status-info-p').innerHTML = 'Closed';
        job.querySelector('.status-info-p').className = 'status-info-p wo-tone-' + woStatusToneSlug('Closed');
      }
      if(job.querySelector('.status-info')){
        job.querySelector('.status-info').style.backgroundColor = '#b88c8c';
        /* job.style.outline = '2px solid #b88c8c'; */
      }
      job.style.fontStyle = "italic";
      job.style.backgroundColor = '#b88c8c';
      const currentData = JSON.parse(job.getAttribute('data-jobinfo'));
      currentData.Status = 'Closed';
      job.setAttribute('data-jobinfo', JSON.stringify(currentData));
      job.remove();
    });
    resolve();
  }

  if(status === 'Ready to Frame' ||
     status === 'Framed' ||
     status === 'Vacuumed' ||
     status === 'Covered' ||
     status === 'Tightened'
  ){
    jobList.forEach(job => {
      if(job.querySelector('.status-info-p')){
        job.querySelector('.status-info-p').innerHTML = status
        job.querySelector('.status-info-p').className = 'status-info-p wo-tone-' + woStatusToneSlug(status);
      }
      if(job.querySelector('.status-info')){
        job.querySelector('.status-info').style.backgroundColor = 'orange';
        /* job.style.outline = '2px solid orange'; */
      }
      const currentData = JSON.parse(job.getAttribute('data-jobinfo'));
      currentData.Status = status;
      job.setAttribute('data-jobinfo', JSON.stringify(currentData));
    });
    resolve();
  }
});
}

export async function updateOperationTableUI(id, data) {

  return new Promise( async (resolve) => {
    const workOrderRow = document.querySelector('.table-item[data-workorder="' + data.Work_Order + '"]');
    const operationRow = document.querySelector('.op-table-data-row[data-id="' + id + '"]');

    if (!workOrderRow) {
      resolve();
      return;
    }

     if(operationRow){
    // '.op-current-status' predates the op-table-data-row redesign
    // (buildOpTableRow() now renders the status pill as '.op-row-status') —
    // the old class is never rendered anymore, so this threw a null
    // dereference on every status change (labor submit, job complete, etc.)
    // whenever the Operations table happened to have this row mounted.
    const statusPillEl = operationRow.querySelector('.op-row-status');
    if (statusPillEl) statusPillEl.innerHTML = data.Status;
    syncOpRowBillButton(operationRow, data);

    switch (data.Status) {
      case 'Ready to Bill':
      case 'Complete':
        operationRow.style.backgroundColor = shell.completeRed;
        break;
      case 'In Progress':
        operationRow.style.backgroundColor = shell.progressYellow;
        break;
      case 'Hold':
        operationRow.style.backgroundColor = 'lightgrey';
        break;
      case 'Closed':
        operationRow.style.backgroundColor = '#b88c8c';
        operationRow.style.fontStyle = 'italic';
        break;
      case 'Ready to Frame':
      case 'Framed':
      case 'Vacuumed':
      case 'Covered':
      case 'Tightened':
        operationRow.style.backgroundColor = 'lightorange';
        break;
      case 'On Work Schedule':
      case 'Needs Scheduling':
        if (statusPillEl) statusPillEl.innerHTML = 'On Work Schedule';
        operationRow.style.backgroundColor = 'initial';
        break;
    }
  }

    const jobs = await getJobsByWorkOrder(data.Work_Order);

        const allOperationStatus = jobs.map(job => job.Status);
    const activeStatuses = allOperationStatus.filter(
      status => status !== 'Declined'
    );

    const someComplete = activeStatuses.some(
      status =>
        status === 'In Progress' ||
        status === 'Ready to Bill' ||
        status === 'Complete' ||
        status === 'Billed' ||
        status === 'Closed'
    );

    const allComplete = activeStatuses.length > 0 && activeStatuses.every(
      status =>
        status === 'Ready to Bill' ||
        status === 'Complete' ||
        status === 'Billed' ||
        status === 'Closed'
    );

    const allClosed = activeStatuses.length > 0 && activeStatuses.every(
      status => status === 'Closed'
    );


    if (allClosed || activeStatuses.length === 0) {
      // activeStatuses.length === 0 means every operation on this work order is
      // now Declined — same "nothing left" end state as allClosed, just not
      // covered by that check since it requires length > 0.
      workOrderRow.remove();
    } else if (allComplete) {
      workOrderRow.querySelector('.status-info-p').innerHTML = 'Ready to Bill';
      workOrderRow.querySelector('.status-info').style.backgroundColor = 'red';
      workOrderRow.classList.add('complete-job');
      
      if(operationRow){
      document.querySelector('.operation-container-row').style.display = 'none';
      }

      /* workOrderRow.style.outline = '2px solid red'; */
    } else if (someComplete) {
      workOrderRow.querySelector('.status-info-p').innerHTML = 'In Progress';
      workOrderRow.querySelector('.status-info').style.backgroundColor = 'yellow';
      /* workOrderRow.style.outline = '2px solid yellow'; */
    }else{
      workOrderRow.querySelector('.status-info-p').innerHTML = 'On Work Schedule';
      workOrderRow.querySelector('.status-info').style.backgroundColor = 'green';
    }

    resolve();
  });
}
